import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import {
  fetchActiveBanners,
  fetchAllPlayerTransactions,
  fetchSeasonEvents,
} from '../../Api'
import type {   
  FeedItem,
  Feed,
  ShopProduct, Banner, TeamStanding, EventGQL, PlayerTransactions } from '../../Api'
import type { HomeFeed } from '../../Api/Feed/feed.types'
import {
  EVENTS_YEAR,
  hasChampSeriesStarted as showChampSeries,
} from '../../Config/League/league.config'
import {
  feedMappingConfig,
  checkHasChampSeriesStarted,
  getCurrentWeek,
  mapLiveEventForSeasonEventsUpdate
} from '../../Utils'
import { getFeedByTag, getHomeFeed } from '../../Api/Feed/feed.api'
import { fetchShopCollection } from '../../Api/Shop/shop.api'
import { SHOP_COLLECTIONS } from '../../Config/Shop/shop.config'
import { ShopProductSortKeys } from '../../Api/Shop/shop.utils'
import { useStorageContext } from '../StorageContext'
import { STORAGE_NAMES, DEFAULT_HOME_FEED } from '../../Constant'
import { leagueReducer, initialState, LEAGUE_ACTIONS } from './reducer'
import { useStandingsContext } from '../../Context/StandingsContext/StandingsContext'

type LeagueContextType = {
  events: EventGQL[]
  eventsLoading: boolean
  currentWeek: null | string
  standings: TeamStanding[]
  feed: Feed | null
  homeFeed: HomeFeed | null
  teamFeed: FeedItem[]
  isFetchingMainFeed: boolean
  isFetchingTeamFeed: boolean
  feedLoading: boolean
  banners: Banner[]
  bestSellingProducts: ShopProduct[]
  playerTransactions: PlayerTransactions[]
  fetchTeamFeed: (teamId: string) => Promise<FeedItem[] | null>
  fetchPlayerTransaction: () => PlayerTransactions[] | Promise<PlayerTransactions[]>
  getTeamEvents: (team: string) => EventGQL[]
  updateEventInState: (event: EventGQL) => void
  fetchEvents: () => Promise<void>
}

export const LeagueContext = createContext<LeagueContextType | null>(null)

export const LeagueProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { standings } = useStandingsContext()
  const { isStorageReady, fetchAndCache, store } = useStorageContext()
  const [state, dispatch] = useReducer(leagueReducer, initialState)
  const mountedRef = useRef(true)

  const fetchData = useCallback(async (): Promise<void> => {
    await Promise.all([
      fetchBanners(),
      fetchEvents(),
      fetchHomeFeed(),
      fetchMainShopCollection(),
    ])
  }, [])

  useEffect(() => {
    mountedRef.current = true
    if (isStorageReady) {
      fetchData()
    }
    return () => {
      mountedRef.current = false
    }
  }, [isStorageReady, fetchData])

  const fetchEvents = useCallback(async (): Promise<void> => {
    dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS_LOADING, payload: true })

    const fetcher = async (): Promise<EventGQL[]> => {
      const result = await fetchSeasonEvents(EVENTS_YEAR, showChampSeries, true)
      return result || []
    }

    const onUpdate = (events: EventGQL[]) => {
      if (!mountedRef.current) return
      if (events.length > 0) {
        dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS, payload: events })
        dispatch({
          type: LEAGUE_ACTIONS.SET_CURRENT_WEEK,
          payload: getCurrentWeek(events),
        })
        dispatch({
          type: LEAGUE_ACTIONS.SET_HAS_CHAMP_SERIES_STARTED,
          payload: checkHasChampSeriesStarted(events),
        })
      }
      dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS_LOADING, payload: false })
    }
    try {
      await fetchAndCache<EventGQL[]>(
        `${STORAGE_NAMES.seasonEvents}_${EVENTS_YEAR}`,
        fetcher,
        onUpdate
      )
    } catch (err) {
      console.error('fetchEvents fetchAndCache error', err)
      dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS_LOADING, payload: false })
    }
  }, [fetchAndCache])

  const fetchBanners = useCallback(async (): Promise<void> => {
    const fetcher = async (): Promise<Banner[]> => {
      const banners = await fetchActiveBanners()
      return banners
    }

    const onUpdate = (banners: Banner[]) => {
      if (!mountedRef.current) return
      dispatch({ type: LEAGUE_ACTIONS.SET_BANNERS, payload: banners })
    }

    try {
      await fetchAndCache<Banner[]>(STORAGE_NAMES.banners, fetcher, onUpdate)
    } catch (err) {
      console.error('fetchBanners fetchAndCache error', err)
    }
  }, [fetchAndCache])

  const fetchMainShopCollection = useCallback(async (): Promise<void> => {
    const fetcher = async (): Promise<ShopProduct[]> => {
      const collection = await fetchShopCollection(
        SHOP_COLLECTIONS.MAIN,
        ShopProductSortKeys.BEST_SELLING,
        10
      )
      return collection?.products || []
    }

    const onUpdate = (products: ShopProduct[]) => {
      if (!mountedRef.current) return
      dispatch({
        type: LEAGUE_ACTIONS.SET_BEST_SELLING_PRODUCTS,
        payload: products,
      })
    }

    try {
      await fetchAndCache<ShopProduct[]>(
        STORAGE_NAMES.home_shop_collection,
        fetcher,
        onUpdate
      )
    } catch (err) {
      console.error('fetchMainShopCollection fetchAndCache error', err)
    }
  }, [fetchAndCache])

  const fetchHomeFeed = useCallback(async (): Promise<void> => {
    dispatch({ type: LEAGUE_ACTIONS.SET_FEED_LOADING, payload: true })
    dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED, payload: true })

    const fetcher = async (): Promise<HomeFeed> => {
      const feedRes = await getHomeFeed({})
      if (!feedRes?.home) return DEFAULT_HOME_FEED

      const feed = Object.keys(DEFAULT_HOME_FEED).reduce((acc, key) => {
        const typedKey = key as keyof HomeFeed
        const mapper = feedMappingConfig[typedKey]
        acc[typedKey] = mapper(feedRes.home)
        return acc
      }, {} as HomeFeed)

      return feed ?? DEFAULT_HOME_FEED
    }

    const onUpdate = (feed: HomeFeed) => {
      if (!mountedRef.current) return
      dispatch({ type: LEAGUE_ACTIONS.SET_HOME_FEED, payload: feed })
      dispatch({ type: LEAGUE_ACTIONS.SET_FEED_LOADING, payload: false })
      dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED, payload: false })
    }
    try {
      await fetchAndCache<HomeFeed>(STORAGE_NAMES.home_feed, fetcher, onUpdate)
    } catch (err) {
      console.error('fetchHomeFeed fetchAndCache error', err)
      dispatch({ type: LEAGUE_ACTIONS.SET_FEED_LOADING, payload: false })
      dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED, payload: false })
    }
  }, [fetchAndCache])

  const fetchTeamFeed = useCallback(
    async (teamId: string): Promise<FeedItem[] | null> => {
      if (state.isFetchingTeamFeed) return null
      dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED, payload: true })

      try {
        const teamFeed = await getFeedByTag({ tags: [teamId] })
        if (!teamFeed?.length) return null
        if (mountedRef.current) {
          dispatch({ type: LEAGUE_ACTIONS.SET_TEAM_FEED, payload: teamFeed })
        }
        dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED, payload: false })
        return teamFeed
      } catch (err) {
        console.error('fetchTeamFeed error', err)
        dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED, payload: false })
        return null
      }
    },
    [state.isFetchingTeamFeed]
  )

  const fetchPlayerTransaction = useCallback(async (): Promise<PlayerTransactions[]> => {
    if (state.playerTransactions.length > 0) {
      return state.playerTransactions
    }
    try {
      const pTransactions = await fetchAllPlayerTransactions()
      const transactions = pTransactions?.playerTransactions || []
      dispatch({ type: LEAGUE_ACTIONS.SET_PLAYER_TRANSACTIONS, payload: transactions })
      return transactions
    } catch (err) {
      console.error('fetchPlayerTransaction error', err)
      return []
    }
  }, [])

  const getTeamEvents = useCallback((team: string): EventGQL[] => {
    return state.events.filter(
      (event) =>
        event.homeTeam?.officialId === team || event.awayTeam?.officialId === team
    )
  }, [])

  const updateEventInState = useCallback(
    (event: EventGQL) => {
      try {
        const updateData = mapLiveEventForSeasonEventsUpdate(event)
        let matched = false
        const updatedEvents = state.events.map((existingEvent) => {
          if (existingEvent.id === event.id) {
            matched = true
            return { ...existingEvent, ...updateData }
          }
          return existingEvent
        })
        if (!matched) return
        dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS, payload: updatedEvents })
        void store(
          `${STORAGE_NAMES.seasonEvents}_${EVENTS_YEAR}`,
          updatedEvents,
          1000 * 60 * 60 * 24 * 3
        ) //3 days
      } catch (err) {
        console.error('updateEventInState error:', err)
      }
    },
    [state.events, store]
  )

  const contextValue = useMemo(
    () => ({
      events: state.events,
      eventsLoading: state.eventsLoading,
      currentWeek: state.currentWeek,
      feed: state.feed,
      homeFeed: state.homeFeed,
      teamFeed: state.teamFeed,
      isFetchingMainFeed: state.isFetchingMainFeed,
      isFetchingTeamFeed: state.isFetchingTeamFeed,
      feedLoading: state.feedLoading,
      banners: state.banners,
      bestSellingProducts: state.bestSellingProducts,
      playerTransactions: state.playerTransactions,
      standings,
      fetchEvents,
      fetchTeamFeed,
      fetchPlayerTransaction,
      getTeamEvents,
      updateEventInState,
    }),
    [
      state.events,
      state.eventsLoading,
      state.currentWeek,
      state.feed,
      state.homeFeed,
      state.teamFeed,
      state.isFetchingMainFeed,
      state.isFetchingTeamFeed,
      state.feedLoading,
      state.banners,
      state.bestSellingProducts,
      state.playerTransactions,
      standings,
      fetchEvents,
      fetchTeamFeed,
      fetchPlayerTransaction,
      getTeamEvents,
      updateEventInState,
    ]
  )

  return <LeagueContext.Provider value={contextValue}>{children}</LeagueContext.Provider>
}

export const useLeagueContext = (): LeagueContextType => {
  try {
    const context = useContext(LeagueContext)
    if (!context) {
      throw new Error(
        'useLeagueContext must be used within a LeagueProvider. Make sure your component is wrapped with LeagueProvider.'
      )   
    }
    return context
  } catch (error) {
    console.error('Error in useLeagueContext:', error)
    throw error
  }
}
